import { USER_MANUALS } from "./data.js";
import type { UserManual } from "./types.js";

export type UserManualsController = {
  refresh(): void;
};

function manualButtonLabel(manual: UserManual): string {
  return `${manual.title} | ${manual.eyebrow}`;
}

function manualMode(manual: UserManual): "guide" | "tutorial" {
  return manual.eyebrow.includes("Tutorial") ? "tutorial" : "guide";
}

function manualUnitLabel(manual: UserManual): string {
  return manualMode(manual) === "tutorial" ? "lessons" : "sections";
}

function sidebarBodyText(manual: UserManual): string {
  return manualMode(manual) === "tutorial"
    ? `This tutorial is designed as a guided course. Work through the ${manual.sections.length} ${manualUnitLabel(
        manual
      )} in order, type every example yourself, and treat the later lessons as practice built on the earlier ones.`
    : `This guide is designed for practical use. Read the big-picture chapter first, then jump into the ${manual.sections.length} ${manualUnitLabel(
        manual
      )} you need while you work in the tool.`;
}

function readerHeaderText(manual: UserManual): string {
  return manualMode(manual) === "tutorial"
    ? "Read the lessons in order if you are new to the language. Each lesson adds one concept, one workflow habit, and one or more short programs you can type and test immediately."
    : "Read the sections in order if you are new to the tool. Jump directly to the interface chapter or the getting-started workflow when you need focused help during real work.";
}

function outlineLabelText(manual: UserManual): string {
  return manualMode(manual) === "tutorial" ? "Jump to lesson" : "Jump to section";
}

function sectionEyebrowText(manual: UserManual, index: number): string {
  return manualMode(manual) === "tutorial" ? `Lesson ${index + 1}` : `Section ${index + 1}`;
}

export function setupUserManuals(panel: HTMLElement): UserManualsController {
  const shell = document.createElement("div");
  shell.className = "manuals-shell";

  const sidebar = document.createElement("aside");
  sidebar.className = "manuals-sidebar";

  const sidebarEyebrow = document.createElement("p");
  sidebarEyebrow.className = "manuals-sidebar__eyebrow";
  sidebarEyebrow.textContent = "User Manuals";

  const sidebarTitle = document.createElement("h3");
  sidebarTitle.className = "manuals-sidebar__title";
  sidebarTitle.textContent = "Long-form guidance for serious use";

  const sidebarBody = document.createElement("p");
  sidebarBody.className = "manuals-sidebar__body";
  sidebarBody.textContent =
    "These manuals are written for students and hobbyists who want workflow guidance, not only a terse reference. Pick a tool or language, read the big picture first, then work through the lessons or the getting-started exercise.";

  const manualNav = document.createElement("div");
  manualNav.className = "manuals-nav";

  const content = document.createElement("section");
  content.className = "manuals-content";

  const hero = document.createElement("header");
  hero.className = "manuals-hero";

  const heroEyebrow = document.createElement("p");
  heroEyebrow.className = "manuals-hero__eyebrow";

  const heroTitle = document.createElement("h2");
  heroTitle.className = "manuals-hero__title";

  const heroSummary = document.createElement("p");
  heroSummary.className = "manuals-hero__summary";

  const heroAudience = document.createElement("p");
  heroAudience.className = "manuals-hero__audience";

  const highlights = document.createElement("div");
  highlights.className = "manuals-highlights";

  const outline = document.createElement("nav");
  outline.className = "manuals-outline";
  outline.setAttribute("aria-label", "Manual section outline");

  const outlineLabel = document.createElement("p");
  outlineLabel.className = "manuals-outline__label";

  const reader = document.createElement("article");
  reader.className = "manuals-reader";

  const navButtons = new Map<string, HTMLButtonElement>();
  let activeManual = USER_MANUALS[0];

  const render = (): void => {
    const mode = manualMode(activeManual);
    heroEyebrow.textContent = activeManual.eyebrow;
    heroTitle.textContent = activeManual.title;
    heroSummary.textContent = activeManual.description;
    heroAudience.textContent = `Recommended for: ${activeManual.audience}`;
    sidebarTitle.textContent = mode === "tutorial" ? "Lesson-by-lesson learning path" : "Detailed guide for practical use";
    sidebarBody.textContent = sidebarBodyText(activeManual);
    outlineLabel.textContent = outlineLabelText(activeManual);

    highlights.textContent = "";
    for (const item of activeManual.highlights) {
      const chip = document.createElement("span");
      chip.className = "manuals-highlight";
      chip.textContent = item;
      highlights.append(chip);
    }

    outline.textContent = "";
    reader.textContent = "";

    const readerHeader = document.createElement("div");
    readerHeader.className = "manuals-reader__header";
    const readerHeaderTitle = document.createElement("h3");
    readerHeaderTitle.textContent = activeManual.title;
    const readerHeaderBody = document.createElement("p");
    readerHeaderBody.textContent = readerHeaderText(activeManual);
    readerHeader.append(readerHeaderTitle, readerHeaderBody);
    reader.append(readerHeader);

    for (const [index, section] of activeManual.sections.entries()) {
      const outlineButton = document.createElement("button");
      outlineButton.type = "button";
      outlineButton.className = "manuals-outline__button";
      outlineButton.textContent = `${index + 1}. ${section.title}`;
      outlineButton.addEventListener("click", () => {
        const target = reader.querySelector<HTMLElement>(`#${section.id}`);
        target?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      outline.append(outlineButton);

      const sectionNode = document.createElement("section");
      sectionNode.className = "manuals-section";
      sectionNode.id = section.id;

      const sectionEyebrow = document.createElement("p");
      sectionEyebrow.className = "manuals-section__eyebrow";
      sectionEyebrow.textContent = sectionEyebrowText(activeManual, index);

      const sectionTitle = document.createElement("h4");
      sectionTitle.className = "manuals-section__title";
      sectionTitle.textContent = section.title;

      const sectionSummary = document.createElement("p");
      sectionSummary.className = "manuals-section__summary";
      sectionSummary.textContent = section.summary;

      const sectionBody = document.createElement("div");
      sectionBody.className = "manuals-section__body";
      sectionBody.innerHTML = section.bodyHtml;

      sectionNode.append(sectionEyebrow, sectionTitle, sectionSummary, sectionBody);
      reader.append(sectionNode);
    }

    for (const [manualId, button] of navButtons) {
      const active = manualId === activeManual.id;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    }
  };

  for (const manual of USER_MANUALS) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "manuals-nav__button";
    button.setAttribute("aria-label", manualButtonLabel(manual));

    const eyebrow = document.createElement("span");
    eyebrow.className = "manuals-nav__eyebrow";
    eyebrow.textContent = manual.eyebrow;

    const title = document.createElement("span");
    title.className = "manuals-nav__title";
    title.textContent = manual.title;

    const body = document.createElement("span");
    body.className = "manuals-nav__body";
    body.textContent = manual.description;

    button.append(eyebrow, title, body);
    button.addEventListener("click", () => {
      activeManual = manual;
      render();
    });
    navButtons.set(manual.id, button);
    manualNav.append(button);
  }

  sidebar.append(sidebarEyebrow, sidebarTitle, sidebarBody, manualNav);
  hero.append(heroEyebrow, heroTitle, heroSummary, heroAudience, highlights);
  content.append(hero, outlineLabel, outline, reader);
  shell.append(sidebar, content);
  panel.replaceChildren(shell);

  render();

  return {
    refresh() {
      render();
    }
  };
}
